import React, { useEffect } from "react";
import { Layout, BookOpen, CheckCircle, Users, Layers } from "lucide-react";

import { ProjectHero } from "../ui/ProjectHero/ProjectHero";
import { VisualShowcase } from "../ui/VisualShowcase/VisualShowcase";
import { ProjectOverview } from "../ui/ProjectOverview/ProjectOverview";
import { BulletItem, BulletList } from "../ui/Bullets/Bullets";
import { KeyFeatures } from "../ui/KeyFeatures/KeyFeatures";
import { ProcessSection } from "../ui/ProcessSection/ProcessSection";
import { ProjectFooter } from "../ui/ProjectFooter/ProjectFooter";
import Footer from "../../../container/InlineFooter/InlineFooter";

import images from "../../../constants/images";

import "./Han.scss";

const Han = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, []);

  const bulletListLeft = (
    <BulletList>
      <BulletItem>
        Reducing friction for researchers creating or customizing cloud-building
        blocks
      </BulletItem>
      <BulletItem>
        Enabling self-service for students and staff without requiring extensive
        technical knowledge
      </BulletItem>
      <BulletItem>
        Simplifying feedback on invalid input or configuration errors
      </BulletItem>
      <BulletItem>
        Ensuring maintainability for SURF’s development team for future
        enhancements
      </BulletItem>
    </BulletList>
  );

  const bulletListRight = (
    <BulletList>
      <BulletItem>
        Replacing complex nested forms with step-by-step guided flows
      </BulletItem>
      <BulletItem>
        Providing contextual links to documentation and clear feedback on
        invalid inputs
      </BulletItem>
      <BulletItem>
        Consolidating the creation and management process into a single-page,
        simplified interface
      </BulletItem>
      <BulletItem>
        Delivering a roadmap for future improvements, empowering SURF’s team to
        continue enhancing the platform independently
      </BulletItem>
    </BulletList>
  );

  return (
    <div className="main-project-container">
      <ProjectHero
        title="Surf Research Cloud"
        subtitle="Simplifying research cloud management for education and scientific institutions"
        client="HAN Univeristy"
        platform="Web portal for researchers and university staff"
        technologies={[
          "React.js",
          " TypeScript",
          " SCSS",
          " REST APIs",
          " Cloud service integrations",
        ]}
        role="Senior Front-End Engineer / UX Collaborator"
        category="ENGINEERING"
        year="2019"
      />
      <VisualShowcase
        imageUrl={images.hanStudent}
        imageAlt="Student from the HAN"
        layout="full"
      />
      <ProjectOverview
        challengeContent={[
          "Making cloud-building accessible and user-friendly",
          "Prior to the project, SURF’s Research Cloud required navigating complex nested forms and documentation-heavy workflows. Challenges included:",
        ]}
        bulletsLeft={bulletListLeft}
        solutionContent={[
          "Streamlined wizards and a single-page flow",
          "The solution was a redesigned portal featuring two intuitive wizards for creating and maintaining cloud-building blocks. Key improvements included:",
        ]}
        bulletsRight={bulletListRight}
        metrics={[
          {
            value: "2x faster",
            label:
              "Cloud-building block creation time compared to previous workflow",
          },
          {
            value: "Single-page",
            label: "Simplified user flow replacing nested forms",
          },
          {
            value: "100%",
            label: "Self-service capability for students and researchers",
          },
          {
            value: "1 roadmap",
            label: "Clear plan provided for ongoing SURF platform improvements",
          },
        ]}
      />
      <VisualShowcase
        title="Creating Cloud-Building Blocks Simplified"
        description="SURF catalog overview with preconfigured building blocks"
        layout="full"
        imageAlt="Surf catalog overview with preconfigured building blocks"
        imageUrl={images.surfCatalog1}
      />
      <VisualShowcase
        title="HAN Student Dashboard Building Block Wizard"
        description="Step-by-step block creation interface wizard with feedback"
        layout="full"
        imageAlt="Surf block creation wizard"
        imageUrl={images.surfCatalog2}
      />
      <KeyFeatures
        title="Streamlined Research Cloud Experience"
        description="Features designed to simplify and empower research workspace creation."
        features={[
          {
            icon: Layout,
            title: "Guided creation wizards",
            description:
              "Step-by-step flows replacing complex nested forms, reducing errors and learning curve.",
          },
          {
            icon: BookOpen,
            title: "Contextual documentation",
            description:
              "Embedded guidance and links ensure researchers can follow best practices without external support.",
          },
          {
            icon: CheckCircle,
            title: "Real-time validation",
            description:
              "Feedback on invalid inputs prevents mistakes and accelerates workflow completion.",
          },
          {
            icon: Users,
            title: "Self-service friendly",
            description:
              "Students and researchers can independently create and manage cloud-building blocks.",
          },
          {
            icon: Layers,
            title: "Maintainable for future expansion",
            description:
              "Roadmap and clean architecture empower SURF developers to iterate and extend the platform.",
          },
        ]}
      />
      <ProcessSection
        title="Designing a User-Friendly Research Cloud"
        description="A collaborative approach focused on simplicity, validation, and self-service empowerment."
        phases={[
          {
            number: "01",
            title: "User Research & Workflow Analysis",
            description:
              "Studied existing workflows, interviewed researchers and staff, and identified pain points in the previous interface.",
            deliverables: [
              "User interviews and personas",
              "Workflow mapping",
              "Pain point documentation",
              "Prioritized feature requirements",
            ],
          },
          {
            number: "02",
            title: "Wireframing & Prototype Design",
            description:
              "Created low- and high-fidelity prototypes for wizards, validating usability with students and researchers.",
            deliverables: [
              "Wireframes for step-by-step flows",
              "High-fidelity clickable prototypes",
              "Usability testing reports",
              "Feedback iteration logs",
            ],
          },
          {
            number: "03",
            title: "Implementation & Integration",
            description:
              "Built the wizards and single-page interface in React/TypeScript, integrated with SURF backend services.",
            deliverables: [
              "React-based wizard implementation",
              "Validation and error handling",
              "Integration with SURF API",
              "Documentation for SURF developers",
            ],
          },
          {
            number: "04",
            title: "Launch & Handover",
            description:
              "Delivered the platform with full documentation and a roadmap for future enhancements, enabling SURF to maintain and extend independently.",
            deliverables: [
              "Deployment to production",
              "Training sessions for SURF staff",
              "Maintenance guide and roadmap",
              "User feedback and iteration plan",
            ],
          },
        ]}
      />
      <ProjectFooter
        impactDescription="This work strengthened SURF’s Research Cloud platform, making cloud-building workflows faster, more intuitive, and fully self-service for students and researchers. By simplifying complex processes, integrating validation, and providing documentation, the project empowered users while enabling SURF’s team to maintain and expand the platform independently."
        impactLabel="Project Impact"
        impactTitle="Empowering researchers and students through simplified self-service"
        nextProject={{
          title: "Ideal Payments Integration",
          link: "/project/ideal",
        }}
        conclusion={{
          label: "Design / Engineering Reflection",
          title: "Simplifying complex systems without sacrificing power",
          description:
            "This project reinforced that building user-friendly platforms isn’t only about frontend polish — it’s about understanding workflows, identifying pain points, and translating technical complexity into accessible experiences. By combining guided wizards, real-time feedback, and clear documentation, we ensured that researchers could work efficiently while SURF’s development team could continue innovating independently. Thoughtful design and engineering empower both users and maintainers, making complex systems feel effortless.",
        }}
      />

      <div
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Footer />
      </div>
    </div>
  );
};

export { Han };

/*
Project Context

The School of Applied Biosciences and Chemistry provides biomedical and chemical research opportunities for students, industry partners, and government organizations. HAN University wanted to integrate cloud-based research workspaces into their educational offerings, leveraging SURF’s Research Cloud platform.

SURF, a cooperative association of Dutch educational and research institutions, develops and shares IT solutions to optimize research and educational workflows. Their Research Cloud service enables researchers to build, manage, and share virtual research workspaces using preconfigured cloud-building blocks or custom setups.

HAN University’s goal was to make this platform more intuitive and self-service friendly for students and researchers alike.


My Role

Front-End Engineer / UX Collaborator

I worked closely with SURF developers, HAN researchers, and UI/UX designers to:

Implement the single-page wizard flows using React and TypeScript

Ensure intuitive UX for both students and technical researchers

Integrate validation, feedback, and documentation links within the forms

Provide guidance on future-proofing the platform for self-service expansion
*/
